/* eslint-disable @typescript-eslint/no-explicit-any */
import SvgIcon from '@mui/material/SvgIcon';
import { FC, memo, lazy, Suspense } from 'react';

// Lazy imports per tutte le icone
const BagsShopping = lazy(
  () => import('../resources/service-icon/bags-shopping.svg?react')
);
const BirthdayCake = lazy(
  () => import('../resources/service-icon/birthday-cake.svg?react')
);
const Briefcase = lazy(
  () => import('../resources/service-icon/briefcase.svg?react')
);
const Camera = lazy(() => import('../resources/service-icon/camera.svg?react'));
const ContactLens = lazy(
  () => import('../resources/service-icon/contact-lens.svg?react')
);
const Child = lazy(() => import('../resources/service-icon/child.svg?react'));
const ConciergeBell = lazy(
  () => import('../resources/service-icon/concierge-bell.svg?react')
);
const Ear = lazy(() => import('../resources/service-icon/ear.svg?react'));
const Eye = lazy(() => import('../resources/service-icon/eye.svg?react'));
const EyeTest = lazy(
  () => import('../resources/service-icon/eye-test.svg?react')
);
const Female = lazy(() => import('../resources/service-icon/female.svg?react'));
const FontCase = lazy(
  () => import('../resources/service-icon/font-case.svg?react')
);
const Gem = lazy(() => import('../resources/service-icon/gem.svg?react'));
const GiftCard = lazy(
  () => import('../resources/service-icon/gift-card.svg?react')
);
const GlassCheers = lazy(
  () => import('../resources/service-icon/glass-cheers.svg?react')
);
const Glasses = lazy(
  () => import('../resources/service-icon/glasses.svg?react')
);
const HandsHeart = lazy(
  () => import('../resources/service-icon/hands-heart.svg?react')
);
const HouseReturn = lazy(
  () => import('../resources/service-icon/house-return.svg?react')
);
const LowVision = lazy(
  () => import('../resources/service-icon/low-vision.svg?react')
);
const Male = lazy(() => import('../resources/service-icon/male.svg?react'));
const ParkingCicle = lazy(
  () => import('../resources/service-icon/parking-circle.svg?react')
);
const PeopleArrows = lazy(
  () => import('../resources/service-icon/people-arrows.svg?react')
);
const Screwdriver = lazy(
  () => import('../resources/service-icon/screwdriver.svg?react')
);
const ShoppingCart = lazy(
  () => import('../resources/service-icon/shopping-cart.svg?react')
);
const Skiing = lazy(() => import('../resources/service-icon/skiing.svg?react'));
const Star = lazy(() => import('../resources/service-icon/star.svg?react'));
const Sunglasses = lazy(
  () => import('../resources/service-icon/sunglasses.svg?react')
);
const Swimmer = lazy(
  () => import('../resources/service-icon/swimmer.svg?react')
);
const TrafficLight = lazy(
  () => import('../resources/service-icon/traffic-light.svg?react')
);
const UserCrown = lazy(
  () => import('../resources/service-icon/user-crown.svg?react')
);
const UserFriends = lazy(
  () => import('../resources/service-icon/user-friends.svg?react')
);
const UserInjured = lazy(
  () => import('../resources/service-icon/user-injured.svg?react')
);
const UserMd = lazy(
  () => import('../resources/service-icon/user-md.svg?react')
);
const UserShield = lazy(
  () => import('../resources/service-icon/user-shield.svg?react')
);
const User = lazy(() => import('../resources/service-icon/user.svg?react'));
const Users = lazy(() => import('../resources/service-icon/users.svg?react'));
const Watch = lazy(() => import('../resources/service-icon/watch.svg?react'));
const Wheelchair = lazy(
  () => import('../resources/service-icon/wheelchair.svg?react')
);

// Fallback più leggero da mostrare durante il caricamento
const IconFallback = () => (
  <SvgIcon
    style={{
      color: 'var(--icon-color)',
      height: '20px',
      width: '20px',
    }}
  />
);

const iconMap: {
  [key: string]: {
    icon: any;
    viewBox: string;
  };
} = {
  'contact-lens': {
    icon: ContactLens,
    viewBox: '0 0 24 24',
  },
  'font-case': {
    icon: FontCase,
    viewBox: '0 0 640 512',
  },
  eye: {
    icon: Eye,
    viewBox: '0 0 576 512',
  },
  'eye-test': {
    icon: EyeTest,
    viewBox: '0 0 24 24',
  },
  'people-arrows': { icon: PeopleArrows, viewBox: '0 0 576 512' },
  'low-vision': { icon: LowVision, viewBox: '0 0 576 512' },
  'house-return': { icon: HouseReturn, viewBox: '0 0 640 512' },
  screwdriver: { icon: Screwdriver, viewBox: '0 0 512 512' },
  glasses: { icon: Glasses, viewBox: '0 0 576 512' },
  sunglasses: { icon: Sunglasses, viewBox: '0 0 576 512' },
  'bags-shopping': { icon: BagsShopping, viewBox: '0 0 576 512' },
  camera: { icon: Camera, viewBox: '0 0 512 512' },
  gem: { icon: Gem, viewBox: '0 0 576 512' },
  briefcase: { icon: Briefcase, viewBox: '0 0 512 512' },
  ear: { icon: Ear, viewBox: '0 0 384 512' },
  child: { icon: Child, viewBox: '0 0 448 512' },
  'glass-cheers': { icon: GlassCheers, viewBox: '0 0 640 512' },
  star: { icon: Star, viewBox: '0 0 576 512' },
  'traffic-light': { icon: TrafficLight, viewBox: '0 0 384 512' },
  watch: { icon: Watch, viewBox: '0 0 384 512' },
  'concierge-bell': { icon: ConciergeBell, viewBox: '0 0 512 512' },
  'user-md': { icon: UserMd, viewBox: '0 0 448 512' },
  'gift-card': { icon: GiftCard, viewBox: '0 0 576 512' },
  'shopping-cart': { icon: ShoppingCart, viewBox: '0 0 576 512' },
  'user-injured': { icon: UserInjured, viewBox: '0 0 448 512' },
  'user-shield': { icon: UserShield, viewBox: '0 0 640 512' },
  'user-crown': { icon: UserCrown, viewBox: '0 0 448 512' },
  'user-friends': { icon: UserFriends, viewBox: '0 0 640 512' },
  female: { icon: Female, viewBox: '0 0 320 512' },
  male: { icon: Male, viewBox: '0 0 256 512' },
  users: { icon: Users, viewBox: '0 0 640 512' },
  swimmer: { icon: Swimmer, viewBox: '0 0 640 512' },
  skiing: { icon: Skiing, viewBox: '0 0 512 512' },
  user: { icon: User, viewBox: '0 0 448 512' },
  'hands-heart': { icon: HandsHeart, viewBox: '0 0 640 512' },
  'parking-circle': { icon: ParkingCicle, viewBox: '0 0 496 512' },
  'birthday-cake': { icon: BirthdayCake, viewBox: '0 0 448 512' },
  wheelchair: { icon: Wheelchair, viewBox: '0 0 512 512' },
};

export const ServiceIconConverter: FC<{ name: string; sx?: any }> = memo(
  ({ name, sx }) => {
    const IconComponent: { icon: any; viewBox: string } | null =
      name in iconMap ? iconMap[name] : null;

    if (IconComponent === null) return null;

    return (
      <Suspense fallback={<IconFallback />}>
        <SvgIcon
          component={IconComponent.icon}
          style={{
            color: 'var(--icon-color)',
            height: '20px',
            width: '20px',
            ...sx,
          }}
          viewBox={IconComponent.viewBox}
        />
      </Suspense>
    );
  }
);
